import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Star as StarIcon } from "../icons";
import { Bookmark as BookmarkIcon } from "../icons";
import { BookmarkBorder as BookmarkBorderIcon } from "../icons";
import FollowItemButton from "./FollowItemButton";
import { MoreVert as MoreVertIcon } from "../icons";
import { Share as ShareIcon } from "../icons";
import { ForumOutlined as ForumOutlinedIcon } from "../icons";
import { ExpandLess as ExpandLessIcon } from "../icons";
import { ExpandMore as ExpandMoreIcon } from "../icons";
import { Send as SendIcon } from "../icons";
import { auth } from "../firebase";
import { T } from "../routes/Translate";
import VerifiedBadge from "../routes/VerifiedBadge";
import OfficialBadge from "../routes/OfficialBadge";
import InterestButton from "./InterestButton";
import { brandColors } from "../theme";
import { feedPostHeaderSx, listingBodySx, listingCardSx, listingContentSx } from "../pageLayout";
import { formatCount, formatDate, reportItem, fetchVerifiedUids, fetchOfficialUids, unfollowCompany } from "../routes/homeUtils";
import { fetchFollowing, handleFollow } from "../routes/eventsUtils";
import { blockUser, unblockUser, fetchBlockedIds } from "../utils/blockedUsers";
import {
  fetchHiddenListingIds,
  fetchSavedListingIds,
  hideListing,
  listingUrl,
  postListingAnswer,
  postListingQuestion,
  subscribeListingQuestions,
  toggleListingLike,
  toggleSaveListing,
} from "../utils/listingSocial";

// Estado por usuario que comparten todas las publicaciones de la página
// (autos o propiedades): sesión, guardados, ocultas ("No me interesa"),
// cuentas seguidas, bloqueados e insignias. Se carga
// una vez en la página y se pasa a cada ListingPost como "social".
export const useListingSocial = (type) => {
  const [uid, setUid] = useState(auth.currentUser?.uid || null);
  const [savedIds, setSavedIds] = useState([]);
  const [hiddenIds, setHiddenIds] = useState([]);
  const [following, setFollowing] = useState([]);
  const [followLoading, setFollowLoading] = useState(false);
  const [blockedIds, setBlockedIds] = useState([]);
  const [verifiedUids, setVerifiedUids] = useState([]);
  const [officialUids, setOfficialUids] = useState([]);

  useEffect(() => auth.onAuthStateChanged((u) => setUid(u?.uid || null)), []);

  useEffect(() => {
    if (!uid) {
      setSavedIds([]);
      setHiddenIds([]);
      setFollowing([]);
      setBlockedIds([]);
      return;
    }
    fetchSavedListingIds(type, uid).then(setSavedIds);
    fetchHiddenListingIds(type, uid).then(setHiddenIds);
    fetchFollowing(setFollowing, setFollowLoading);
    fetchBlockedIds(uid).then(setBlockedIds);
    fetchVerifiedUids().then(setVerifiedUids);
    fetchOfficialUids().then(setOfficialUids);
  }, [type, uid]);

  const toggleSave = useCallback((id) => {
    if (!uid) return;
    const isSaved = savedIds.includes(id);
    toggleSaveListing(type, uid, id, isSaved);
    setSavedIds((prev) => (isSaved ? prev.filter((x) => x !== id) : [...prev, id]));
  }, [type, uid, savedIds]);

  const hide = useCallback((id) => {
    if (!uid) return;
    hideListing(type, uid, id);
    setHiddenIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, [type, uid]);

  const follow = useCallback((companyId) => {
    handleFollow(companyId, following, setFollowing, setFollowLoading);
  }, [following]);

  const unfollow = useCallback((companyId) => {
    unfollowCompany(companyId, uid, setFollowing);
  }, [uid]);

  // Para filtrar el feed: sin las ocultas ni las de cuentas bloqueadas.
  const isVisible = useCallback(
    (item) => !hiddenIds.includes(item.id) && !blockedIds.includes(item.uid),
    [hiddenIds, blockedIds],
  );

  const toggleBlock = useCallback(async (targetUid) => {
    if (!uid) return;
    if (blockedIds.includes(targetUid)) {
      await unblockUser(uid, targetUid);
      setBlockedIds((prev) => prev.filter((x) => x !== targetUid));
    } else {
      await blockUser(uid, targetUid);
      setBlockedIds((prev) => [...prev, targetUid]);
    }
  }, [uid, blockedIds]);

  return {
    type, uid, savedIds, following, followLoading, blockedIds, verifiedUids, officialUids,
    toggleSave, hide, follow, unfollow, toggleBlock, isVisible,
  };
};

// Publicación de autos/propiedades con el mismo formato que las del feed de
// home/events: cabecera con la cuenta (seguir, guardar, compartir y menú ⋮),
// foto, datos, "Me Interesa" y preguntas y respuestas. `media` es la foto (con
// su contenedor) y `children` los datos de la publicación. `onSimilar` filtra
// la página por publicaciones parecidas ("Más ... similares").
const ListingPost = ({ item, followType, social, photo, featured, media, children, onSimilar, similarLabel }) => {
  const {
    type, uid, savedIds, following, followLoading, blockedIds, verifiedUids, officialUids,
    toggleSave, hide, follow, unfollow, toggleBlock,
  } = social;
  const isFollowed = following.includes(item.uid);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [qa, setQa] = useState([]);
  const [showQA, setShowQA] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState({});

  useEffect(() => subscribeListingQuestions(type, item.id, setQa), [type, item.id]);

  const liked = !!uid && (item.likes || []).includes(uid);
  const isOwner = uid === item.uid;

  const closeMenu = () => setMenuAnchor(null);

  const handleShare = () => {
    const url = listingUrl(type, item.id);
    if (navigator.share) {
      navigator.share({ title: item.title, url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url).catch(() => {});
    }
  };

  const handleQuestionSubmit = async () => {
    const text = newQuestion.trim();
    if (!text || !uid) return;
    await postListingQuestion(type, item.id, uid, text);
    setNewQuestion("");
  };

  const handleAnswerSubmit = async (questionId) => {
    const text = newAnswer[questionId]?.trim();
    if (!text || !uid) return;
    await postListingAnswer(type, item.id, questionId, uid, text);
    setNewAnswer((prev) => ({ ...prev, [questionId]: "" }));
  };

  return (
    <Box sx={{ ...listingCardSx, backgroundColor: brandColors.backgroundPaper }}>
      <Box sx={{ ...headerSx, ...feedPostHeaderSx }}>
        <Avatar
          src={photo || item.image}
          alt={item.name}
          sx={{ width: 40, height: 40, mr: 2, flexShrink: 0, border: `2.5px solid ${brandColors.divider}` }}
        />
        <Link to={`/profile/${item.uid}`} style={{ textDecoration: "none", display: "flex", alignItems: "center", minWidth: 0, flexShrink: 1, overflow: "hidden" }}>
          <Typography sx={{ color: brandColors.accent, fontSize: "1rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {item.name || <T>Usuario</T>}
          </Typography>
          {verifiedUids.includes(item.uid) && <VerifiedBadge />}
          {officialUids.includes(item.uid) && <OfficialBadge />}
        </Link>

        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, ml: "auto", flexShrink: 0 }}>
          {featured && (
            <Chip
              icon={<StarIcon sx={{ fontSize: "9px !important", ml: "3px !important" }} />}
              label={<T>Destacado</T>}
              size="small"
              sx={{
                height: 14,
                bgcolor: brandColors.accent,
                color: "#fff",
                "& .MuiChip-icon": { color: "#fff" },
                "& .MuiChip-label": { fontSize: "0.55rem", px: "4px", fontWeight: 600 },
              }}
            />
          )}
          {uid && !isOwner && !isFollowed && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => follow(item.uid)}
              disabled={followLoading}
              sx={{ flexShrink: 0, textTransform: "none", borderColor: brandColors.primary, color: brandColors.accent }}
            >
              <T>Seguir</T>
            </Button>
          )}
          {uid && (
            <IconButton
              onClick={() => toggleSave(item.id)}
              aria-label={savedIds.includes(item.id) ? "quitar de guardados" : "guardar"}
              sx={{ p: { xs: 0.5, sm: 1 } }}
            >
              {savedIds.includes(item.id) ? <BookmarkIcon /> : <BookmarkBorderIcon />}
            </IconButton>
          )}
          {followType && <FollowItemButton type={followType} item={item} />}
          <IconButton onClick={handleShare} aria-label="share" sx={{ p: { xs: 0.5, sm: 1 } }}>
            <ShareIcon />
          </IconButton>
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="more options" sx={{ p: { xs: 0.5, sm: 1 } }}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={listingBodySx}>
        {media}
        <Box sx={{ ...listingContentSx, pb: 0 }}>
          {children}
          <InterestButton
            liked={liked}
            count={formatCount(item.helpedCount || 0)}
            onClick={() => toggleListingLike(type, item.id, uid)}
            disabled={!uid}
          />
          {item.createdAt && (
            <Typography sx={{ fontSize: "0.8rem", color: brandColors.textSecondary }}>
              <T>Publicado:</T> {formatDate(item.createdAt)}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mt: { xs: 0, sm: 3 }, borderTop: `1px solid ${brandColors.divider}`, pt: 2, px: { xs: 1.5, sm: 0 }, pb: { xs: 1.5, sm: 0 } }}>
        <Box onClick={() => setShowQA((v) => !v)} sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}>
          <ForumOutlinedIcon sx={{ fontSize: 20, color: brandColors.textSecondary }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1, display: "flex", alignItems: "center" }}>
            <T>Preguntas y Respuestas</T>
            {qa.length > 0 && (
              <Box component="span" sx={{ ml: 0.75, px: 1, py: 0.125, borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, backgroundColor: "rgba(127,127,127,0.25)" }}>
                {qa.length}
              </Box>
            )}
          </Typography>
          {showQA ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>

        {showQA && (
          <>
            {qa.length === 0 && (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <ForumOutlinedIcon sx={{ fontSize: 32, color: brandColors.divider, mb: 1 }} />
                <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                  <T>Todavía no hay preguntas. ¡Sé el primero en preguntar!</T>
                </Typography>
              </Box>
            )}
            <List dense sx={{ p: 0, mt: 1 }}>
              {qa.slice(0, showAll ? undefined : 1).map((q) => (
                <ListItem key={q.id} alignItems="flex-start" sx={{ p: 0, mb: 1.5 }}>
                  <Paper variant="outlined" sx={{ p: 1.75, width: "100%", borderRadius: 2, bgcolor: brandColors.backgroundPaper, borderColor: brandColors.divider }}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Avatar src={q.profileImageUrl} alt={q.displayName} sx={{ width: 32, height: 32 }} />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{q.displayName}</Typography>
                          {q.createdAt && (
                            <Typography variant="caption" sx={{ color: brandColors.textSecondary }}>{formatDate(q.createdAt)}</Typography>
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>{q.question}</Typography>
                      </Box>
                    </Box>
                    {q.answers.map((a) => (
                      <Box key={a.id} sx={{ display: "flex", gap: 1, mt: 1.5, pl: 2, borderLeft: `2px solid ${brandColors.divider}` }}>
                        <Avatar src={a.profileImageUrl} alt={a.displayName} sx={{ width: 26, height: 26 }} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: brandColors.accent }}>{a.displayName}</Typography>
                            {a.createdAt && (
                              <Typography variant="caption" sx={{ color: brandColors.textSecondary }}>{formatDate(a.createdAt)}</Typography>
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>{a.answer}</Typography>
                        </Box>
                      </Box>
                    ))}
                    {isOwner && !q.answers.length && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, pl: 2 }}>
                        <TextField
                          fullWidth
                          variant="standard"
                          size="small"
                          placeholder="Escribe tu respuesta..."
                          value={newAnswer[q.id] || ""}
                          onChange={(e) => setNewAnswer((prev) => ({ ...prev, [q.id]: e.target.value }))}
                        />
                        <IconButton size="small" color="primary" onClick={() => handleAnswerSubmit(q.id)} disabled={!newAnswer[q.id]?.trim()}>
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                </ListItem>
              ))}
            </List>

            {qa.length > 1 && (
              <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ textTransform: "none", fontSize: "0.8rem" }}>
                {showAll ? <T>Mostrar menos</T> : <><T>Mostrar</T> {qa.length - 1} <T>más</T></>}
              </Button>
            )}

            {uid && !isOwner && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                <TextField
                  fullWidth
                  placeholder="Escribe una pregunta..."
                  size="small"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleQuestionSubmit(); }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "999px", bgcolor: brandColors.inputBackground },
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                />
                <IconButton color="primary" onClick={handleQuestionSubmit} disabled={!newQuestion.trim()}>
                  <SendIcon />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Mismas opciones y en el mismo orden que el menú de home/events. */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {isFollowed && (
          <MenuItem onClick={() => { unfollow(item.uid); closeMenu(); }}>
            <T>Dejar de seguir</T>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (uid) reportItem({ reporterId: uid, targetType: type, targetId: item.id, companyId: item.uid });
            closeMenu();
          }}
        >
          <T>Reportar</T>
        </MenuItem>
        <MenuItem onClick={() => { hide(item.id); closeMenu(); }}>
          <T>No me interesa</T>
        </MenuItem>
        {onSimilar && (
          <MenuItem onClick={() => { onSimilar(item); closeMenu(); }}>
            {similarLabel}
          </MenuItem>
        )}
        {uid && !isOwner && (
          <MenuItem onClick={() => { toggleBlock(item.uid); closeMenu(); }}>
            {blockedIds.includes(item.uid) ? <T>Desbloquear</T> : <T>Bloquear</T>}
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

const headerSx = {
  display: "flex",
  alignItems: "center",
  flexWrap: "nowrap",
  padding: "12px 16px",
  borderBottom: `1px solid ${brandColors.divider}`,
  backgroundColor: brandColors.backgroundDefault,
};

export default ListingPost;
